import { motion } from 'framer-motion';

interface StatusBadgeProps {
  status: 'online' | 'offline' | 'maintenance';
  pulse?: boolean;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function StatusBadge({
  status,
  pulse = true,
  label,
  size = 'md',
}: StatusBadgeProps) {
  const statusConfig = {
    online: {
      color: 'bg-green-500',
      glowColor: 'rgba(34, 197, 94, 0.6)',
      text: 'Online',
      textColor: 'text-green-400',
    },
    offline: {
      color: 'bg-red-500',
      glowColor: 'rgba(239, 68, 68, 0.6)',
      text: 'Offline',
      textColor: 'text-red-400',
    },
    maintenance: {
      color: 'bg-yellow-500',
      glowColor: 'rgba(234, 179, 8, 0.6)',
      text: 'Maintenance',
      textColor: 'text-yellow-400',
    },
  };

  const sizeConfig = {
    sm: {
      dot: 'w-2 h-2',
      text: 'text-xs',
      padding: 'px-2 py-1',
      gap: 'gap-1.5',
    },
    md: {
      dot: 'w-3 h-3',
      text: 'text-sm',
      padding: 'px-3 py-1.5',
      gap: 'gap-2',
    },
    lg: {
      dot: 'w-4 h-4',
      text: 'text-base',
      padding: 'px-4 py-2',
      gap: 'gap-2.5',
    },
  };

  const config = statusConfig[status];
  const sizes = sizeConfig[size];
  const displayLabel = label || config.text;

  return (
    <motion.div
      className={`inline-flex items-center ${sizes.gap} ${sizes.padding} rounded-full bg-bg-elevated/80 border border-border-primary backdrop-blur-sm`}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      whileHover={{ scale: 1.05 }}
      title={`Status: ${config.text}`}
    >
      {/* Status dot with pulse animation */}
      <div className="relative">
        <motion.div
          className={`${sizes.dot} rounded-full ${config.color}`}
          style={{
            boxShadow: pulse ? `0 0 10px ${config.glowColor}` : 'none',
          }}
        />
        
        {/* Pulse ring animation */}
        {pulse && status === 'online' && (
          <motion.div
            className={`absolute inset-0 ${sizes.dot} rounded-full ${config.color}`}
            animate={{
              scale: [1, 2, 2],
              opacity: [0.7, 0.3, 0],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeOut",
            }}
          />
        )}
        
        {/* Slow blink for maintenance */}
        {pulse && status === 'maintenance' && (
          <motion.div
            className={`absolute inset-0 ${sizes.dot} rounded-full ${config.color}`}
            animate={{
              opacity: [1, 0.3, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        )}
      </div>

      {/* Status label */}
      <span className={`${sizes.text} font-medium ${config.textColor} font-mono`}>
        {displayLabel}
      </span>
    </motion.div>
  );
}
